import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { IntegrationsFacade } from '@features/integrations/data-access/integrations.facade';
import { scopeGuard } from '@core/guards/scope.guard';
import { superadminGuard } from '@core/guards/superadmin.guard';
import { HeaderVariant } from '@shared/layout/header/enums/header-variant.enum';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('@shared/layout/shell-layout/shell-layout.component').then(
        (m) => m.ShellLayoutComponent,
      ),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      {
        path: 'home',
        loadComponent: () =>
          import('@features/ai/pages/ai/ai.component').then((m) => m.AiComponent),
        data: {
          hideShellHeader: true,
        },
      },
      {
        path: 'home-demo',
        loadComponent: () =>
          import('@features/home/pages/home/home.component').then((m) => m.HomeComponent),
        data: { headerVariant: HeaderVariant.Home },
      },
      {
        path: 'investigate',
        loadComponent: () =>
          import('@features/investigate/pages/investigate/investigate.component').then(
            (m) => m.InvestigateComponent,
          ),
        data: {
          headerVariant: HeaderVariant.Standard,
          pageTitleKey: 'investigate.pageTitle',
          pageSubtitleKey: 'investigate.pageSubtitle',
        },
      },
      {
        path: 'experiments',
        loadComponent: () =>
          import('@features/experiments/pages/experiments/experiments.component').then(
            (m) => m.ExperimentsComponent,
          ),
        data: {
          headerVariant: HeaderVariant.Experiments,
          pageTitleKey: 'experiments.pageTitle',
          pageSubtitleKey: 'experiments.pageSubtitle',
        },
      },
      {
        path: 'autopilot',
        loadComponent: () =>
          import('@features/autopilot/pages/autopilot/autopilot.component').then(
            (m) => m.AutopilotComponent,
          ),
        data: {
          headerVariant: HeaderVariant.Standard,
          pageTitleKey: 'autopilot.pageTitle',
          pageSubtitleKey: 'autopilot.pageSubtitle',
        },
      },
      {
        path: 'reports',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@features/reports/pages/reports/reports.component').then(
                (m) => m.ReportsComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Reports,
              pageTitleKey: 'reports.pageTitle',
              pageSubtitleKey: 'reports.pageSubtitle',
            },
          },
          {
            path: 'templates',
            loadComponent: () =>
              import('@features/reports/pages/reports-templates/reports-templates.component').then(
                (m) => m.ReportsTemplatesComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'reports.templates.pageTitle',
              pageSubtitleKey: 'reports.templates.pageSubtitle',
            },
          },
          {
            path: 'builder',
            loadComponent: () =>
              import('@features/reports/pages/reports-builder/reports-builder.component').then(
                (m) => m.ReportsBuilderComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'reports.builder.pageTitle',
              pageSubtitleKey: 'reports.builder.pageSubtitleDescribe',
            },
          },
          {
            path: 'builder/:instanceId',
            loadComponent: () =>
              import('@features/reports/pages/reports-builder/reports-builder.component').then(
                (m) => m.ReportsBuilderComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'reports.builder.pageTitle',
              pageSubtitleKey: 'reports.builder.pageSubtitleConfigure',
            },
          },
          {
            path: 'history',
            loadComponent: () =>
              import('@features/reports/pages/reports-history/reports-history.component').then(
                (m) => m.ReportsHistoryComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'reports.history.pageTitle',
              pageSubtitleKey: 'reports.history.pageSubtitle',
            },
          },
          {
            path: 'executions/:id',
            loadComponent: () =>
              import('@features/reports/pages/reports-execution/reports-execution.component').then(
                (m) => m.ReportsExecutionComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'reports.execution.pageTitle',
              pageSubtitleKey: 'reports.execution.pageSubtitle',
            },
          },
        ],
      },
      {
        path: 'ai',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'internal/missions/:missionId',
        canActivate: [scopeGuard],
        loadComponent: () =>
          import('@features/mission-debug/pages/mission-debug/mission-debug.component').then(
            (m) => m.MissionDebugComponent,
          ),
        data: {
          headerVariant: HeaderVariant.Standard,
          pageTitleKey: 'missionDebug.pageTitle',
          pageSubtitleKey: 'missionDebug.pageSubtitle',
          scopes: ['agent_debug.view'],
        },
      },
      {
        path: 'integrations',
        providers: [IntegrationsFacade],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@features/integrations/pages/integrations/integrations.component').then(
                (m) => m.IntegrationsComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'integrations.pageTitle',
              pageSubtitleKey: 'integrations.pageSubtitle',
            },
          },
          {
            path: ':provider',
            loadComponent: () =>
              import('@features/integrations/pages/integrations-provider/integrations-provider.component').then(
                (m) => m.IntegrationsProviderComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'integrations.provider.pageTitle',
              pageSubtitleKey: 'integrations.provider.pageSubtitle',
            },
          },
        ],
      },
      {
        path: 'admin',
        canActivate: [superadminGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@features/admin/pages/admin/admin.component').then((m) => m.AdminComponent),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'admin.pageTitle',
              pageSubtitleKey: 'admin.pageSubtitle',
            },
          },
          {
            path: 'report-templates',
            loadComponent: () =>
              import('@features/admin/pages/admin-report-templates/admin-report-templates.component').then(
                (m) => m.AdminReportTemplatesComponent,
              ),
            data: {
              headerVariant: HeaderVariant.Standard,
              pageTitleKey: 'admin.templates.pageTitle',
              pageSubtitleKey: 'admin.templates.pageSubtitle',
            },
          },
        ],
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('@features/settings/pages/settings/settings.component').then(
            (m) => m.SettingsComponent,
          ),
        data: {
          headerVariant: HeaderVariant.Standard,
          pageTitleKey: 'settings.pageTitle',
          pageSubtitleKey: 'settings.pageSubtitle',
        },
      },
    ],
  },
  {
    path: '',
    loadChildren: () => import('@features/auth/auth.routes').then((m) => m.authRoutes),
  },
  {
    path: 'share/:shareToken',
    loadComponent: () =>
      import('@features/ai/pages/shared-conversation/shared-conversation.component').then(
        (m) => m.SharedConversationComponent,
      ),
  },
  { path: '**', redirectTo: 'home' },
];
